import { UserRoles } from '@constants';

/**
 * First-time guide experience: a guide under a company with tours picks theirs
 * before going on. The backend (GET tip-direct-tours/selection-status) decides
 * whether the gate is needed; these helpers only cover the client side.
 */

// Never interrupt onboarding or the payout flow; the gate catches the guide on
// the next page they land on instead.
const EXEMPT_PREFIXES = ['/onboarding', '/payoutlink'];

export const isExemptPath = path =>
  typeof path === 'string' && EXEMPT_PREFIXES.some(prefix => path.toLowerCase().startsWith(prefix));

// Only invited guides are gated, so everyone else skips the request entirely.
export const shouldCheckTourSelection = user =>
  !!user && !!user.userId && user.role === UserRoles.TapToTipAgent;

const normalise = text =>
  `${text || ''}`
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

/** Every word typed must appear in the title or city, in any order. */
export const filterTours = (tours, query) => {
  const words = normalise(query)
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return tours;
  return tours.filter(tour => {
    const haystack = `${normalise(tour.title)} ${normalise(tour.city)}`;
    return words.every(word => haystack.includes(word));
  });
};

export const toggleId = (selected, id) => {
  const next = new Set(selected);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
};

/** Select or clear every tour currently shown, leaving hidden picks alone. */
export const setVisible = (selected, visibleTours, on) => {
  const next = new Set(selected);
  visibleTours.forEach(tour => (on ? next.add(tour._id) : next.delete(tour._id)));
  return next;
};

/** Distinct cities with tour counts, most tours first, for the city filter. */
export const cityCounts = tours => {
  const counts = new Map();
  (tours || []).forEach(tour => {
    const key = `${tour.city || ''}`.trim().toLowerCase();
    if (key) counts.set(key, (counts.get(key) || 0) + 1);
  });
  return [...counts.entries()]
    .map(([key, count]) => ({ key, label: key.replace(/(^|[\s-])\S/g, c => c.toUpperCase()), count }))
    .sort((a, b) => b.count - a.count || a.key.localeCompare(b.key));
};

export const allVisibleSelected = (selected, visibleTours) =>
  visibleTours.length > 0 && visibleTours.every(tour => selected.has(tour._id));

/*
 * Other auto-opening overlays (the What's new dialog) must wait while the gate
 * is up: a dialog on top of it steals focus from the search box and buries the
 * Continue button. The gate marks <body> and fires an event so they can tell
 * without importing this component.
 */
export const TOUR_SELECTION_ATTR = 'data-tour-selection-open';
export const TOUR_SELECTION_EVENT = 'tourselection:change';

export const isTourSelectionOpen = () =>
  typeof document !== 'undefined' && document.body.getAttribute(TOUR_SELECTION_ATTR) === 'true';

export const markTourSelectionOpen = open => {
  if (typeof document === 'undefined') return;
  if (open) document.body.setAttribute(TOUR_SELECTION_ATTR, 'true');
  else document.body.removeAttribute(TOUR_SELECTION_ATTR);
  window.dispatchEvent(new CustomEvent(TOUR_SELECTION_EVENT, { detail: { open } }));
};
